import { useTranslation } from 'react-i18next'
import { Skeleton, StepList } from '@/ds'
import type { Step } from '@/ds'
import { listJoin } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { changeQuery } from '../../Reserva/shared'
import { asyncValue } from '@/lib/useAsync'
import type { Stay } from '@/services/reserva'
import { allDone, buildSteps, doneShort } from '../steps'
import type { StayStep } from '../steps'
import type { StayData } from '../useStayData'
import { SectionError } from './SectionError'

interface Props {
  stay: Stay
  data: StayData
  lang: string
}

export function useSteps(stay: Stay, data: StayData, lang: string): { steps: StayStep[]; loading: boolean } {
  const { t } = useTranslation()
  const loading = data.payment.kind === 'loading' || data.deposit.kind === 'loading' || data.arrival.kind === 'loading' || data.key.kind === 'loading'
  const steps = loading
    ? []
    : buildSteps({
        stay,
        payment: asyncValue(data.payment),
        deposit: asyncValue(data.deposit),
        arrival: data.arrival.kind === 'unsupported' ? 'unsupported' : asyncValue(data.arrival),
        key: asyncValue(data.key),
        lang,
        t,
      })
  return { steps, loading }
}

/** Lista de pasos de 8.1 con sus estados de carga y error; colapsa en "Todo listo" cuando todo está hecho. */
export function StepsSection({ stay, data, lang }: Props) {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const { steps, loading } = useSteps(stay, data, lang)

  if (loading) {
    return (
      <div className="estancia__skeletons" aria-busy="true">
        <span className="visually-hidden">{t('v2.comun.unMomento')}</span>
        <Skeleton height={64} radius={16} />
        <Skeleton height={64} radius={16} />
        <Skeleton height={64} radius={16} />
      </div>
    )
  }

  // Pago y fianza vuelven a Tu estancia al terminar o al ir atrás (B4).
  const goStep = (route: string) => (route.startsWith('/reserva/pagos') || route.startsWith('/reserva/fianza') ? go(route, { query: changeQuery('estancia') }) : go(route))
  const failed = [data.payment, data.deposit, data.key].find((a) => a.kind === 'error')
  const items: Step[] = steps.map((s) => ({
    id: s.id,
    title: s.title,
    subtitle: s.subtitle,
    state: s.state,
    ...(s.actionLabel && s.route ? { action: { label: s.actionLabel, variant: s.actionVariant, onClick: () => goStep(s.route!) } } : {}),
  }))
  return (
    <>
      {items.length > 0 && (
        <StepList
          steps={items}
          onSelect={(step) => {
            const route = steps.find((s) => s.id === step.id)?.route
            if (route) goStep(route)
          }}
          allDoneDetail={allDone(steps) ? listJoin(doneShort(steps, t), lang) : undefined}
        />
      )}
      {failed?.kind === 'error' && <SectionError code={failed.code} onRetry={data.reload} />}
    </>
  )
}
